import { AccessController } from '@adornis/abac/core/AccessController.js';
import type { Request } from '@adornis/abac/core/Request.js';
import type { IPayload, IPolicy } from '@adornis/abac/core/types.js';
import { ServerContextPolicy } from '@adornis/abac/policies/ServerContextPolicy.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { PDFFile } from '@adornis/pdf/db/pdf-file.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { AppFeedback } from '../app-feedpack.js';
import { ClassMembership } from '../class-membership.js';
import { ClassRoom } from '../classroom.js';
import { Conversation } from '../conversation.js';
import { Exam } from '../exam.js';
import { ExamResultRelease } from '../exam-result-release.js';
import { Feedback } from '../feedback.js';
import { Student } from '../student.js';
import { Submission } from '../submission.js';
import { Teacher } from '../teacher.js';
import { getSubject, ownerIds, type Subject } from './subject.js';

export type EntityAction = 'read' | 'create' | 'update' | 'delete';

/** Not an entity — the job queue's raw collection (api/jobs/job.ts). Authorized through its exam. */
export const EXAM_JOB_CLASS = 'ExamJob';

export const FILE_CLASSES: readonly string[] = [AdornisFile._class, ImageFile._class, PDFFile._class];

const PAYLOAD_TYPE = 'gradias/entity';

/**
 * Carries the already-loaded document, so a rule is a pure-ish function of (subject, doc) and never fetches
 * the document a second time. `doc` is the raw Mongo document (or, for create, the one about to be inserted).
 */
export interface EntityPayload extends IPayload {
  type: typeof PAYLOAD_TYPE;
  entityClass: string;
  action: EntityAction;
  doc: any;
}

export const createEntityPayload = (entityClass: string, action: EntityAction, doc: any): EntityPayload => ({
  type: PAYLOAD_TYPE,
  entityClass,
  action,
  doc,
});

export type Rule = (subject: Subject, action: EntityAction, doc: any) => Promise<boolean> | boolean;

const isOwner = (subject: Subject, ownerId: unknown) =>
  typeof ownerId === 'string' && ownerIds(subject).includes(ownerId);

async function ownsExam(subject: Subject, examId: unknown): Promise<boolean> {
  if (typeof examId !== 'string' || !examId) return false;
  const exam = await (await getRawCollection(Exam._collectionName)).findOne(
    { _id: examId as any },
    { projection: { userID: 1 } },
  );
  return isOwner(subject, exam?.userID);
}

async function hasMembership(studentId: string | undefined, classRoomID: unknown, statuses: string[]) {
  if (!studentId || typeof classRoomID !== 'string') return false;
  const membership = await (await getRawCollection(ClassMembership._collectionName)).findOne(
    { classRoomID, studentID: studentId, status: { $in: statuses } },
    { projection: { _id: 1 } },
  );
  return Boolean(membership);
}

async function ownsClassRoom(subject: Subject, classRoomID: unknown): Promise<boolean> {
  if (!subject.teacherId || typeof classRoomID !== 'string') return false;
  const classroom = await (await getRawCollection(ClassRoom._collectionName)).findOne(
    { _id: classRoomID as any },
    { projection: { teacherID: 1 } },
  );
  return classroom?.teacherID === subject.teacherId;
}

const fileRule: Rule = (subject, action, doc) => {
  // Files created before owner stamping have no `ownerUserID` and are denied — decided, no backfill.
  if (action === 'create') return ownerIds(subject).length > 0;
  if (isOwner(subject, doc?.ownerUserID)) return true;
  return (
    action === 'read' &&
    Boolean(subject.userID) &&
    Array.isArray(doc?.readerUserIDs) &&
    doc.readerUserIDs.includes(subject.userID)
  );
};

/** Exported for the unit tests (tests/authz/policies.test.ts); everything else goes through `accessController`. */
export const rules: Record<string, Rule> = {
  [Exam._class]: (subject, _action, doc) => isOwner(subject, doc?.userID),

  [Submission._class]: async (subject, action, doc) => {
    if (action !== 'create' && isOwner(subject, doc?.userID)) return true;
    return ownsExam(subject, doc?.examId);
  },

  [Conversation._class]: (subject, _action, doc) => isOwner(subject, doc?.userID),

  // Profiles belong to a real account only — a guest never has one.
  [Teacher._class]: (subject, _action, doc) => Boolean(subject.userID) && doc?.userID === subject.userID,
  [Student._class]: (subject, _action, doc) => Boolean(subject.userID) && doc?.userID === subject.userID,

  [ClassRoom._class]: async (subject, action, doc) => {
    if (subject.teacherId && doc?.teacherID === subject.teacherId) return true;
    // Members see the class; stripping the join `code` for them is the resolver's job.
    return action === 'read' && hasMembership(subject.studentId, doc?._id, ['pending', 'approved']);
  },

  [ClassMembership._class]: (subject, action, doc) => {
    const isStudent = Boolean(subject.studentId) && doc?.studentID === subject.studentId;
    const isTeacher = Boolean(subject.teacherId) && doc?.teacherID === subject.teacherId;
    if (action === 'read') return isStudent || isTeacher;
    if (action === 'create') return isStudent;
    return isTeacher;
  },

  [ExamResultRelease._class]: async (subject, action, doc) => {
    if (
      action === 'read' &&
      Boolean(subject.studentId) &&
      doc?.studentID === subject.studentId &&
      (await hasMembership(subject.studentId, doc?.classRoomID, ['approved']))
    ) {
      return true;
    }
    return (await ownsExam(subject, doc?.examId)) && (await ownsClassRoom(subject, doc?.classRoomID));
  },

  // Write-only for clients; reading feedback is an internal (server context) concern.
  [Feedback._class]: async (subject, action, doc) =>
    action === 'create' && Boolean(subject.userID) && isOwnConversation(subject, doc?.conversationId),
  [AppFeedback._class]: (subject, action, doc) =>
    action === 'create' && Boolean(subject.userID) && doc?.userId === subject.userID,

  [AdornisFile._class]: fileRule,
  [ImageFile._class]: fileRule,
  [PDFFile._class]: fileRule,

  // Self — and, while impersonating, the admin behind it: @adornis/users subscribes to both for the banner.
  [AdornisUser._class]: (subject, action, doc) =>
    action === 'read' &&
    Boolean(subject.userID) &&
    (doc?._id === subject.userID || (Boolean(subject.impersonatingUserID) && doc?._id === subject.impersonatingUserID)),

  [EXAM_JOB_CLASS]: (subject, action, doc) => action === 'read' && ownsExam(subject, doc?.examId),
};

async function isOwnConversation(subject: Subject, conversationId: unknown): Promise<boolean> {
  if (typeof conversationId !== 'string') return false;
  const conversation = await (await getRawCollection(Conversation._collectionName)).findOne(
    { _id: conversationId as any },
    { projection: { userID: 1 } },
  );
  return isOwner(subject, conversation?.userID);
}

const entityPolicies: IPolicy[] = Object.entries(rules).map(([entityClass, rule]) => ({
  name: `${entityClass}Policy`,
  acceptingPayloadTypes: [PAYLOAD_TYPE],
  async evaluate(request: Request) {
    const payload = request.payload as EntityPayload;
    if (payload.entityClass !== entityClass) return false;
    const allowed = await rule(await getSubject(), payload.action, payload.doc);
    return { allowed, reason: `${entityClass}: ${payload.action} ${allowed ? 'allowed' : 'denied'}` };
  },
}));

/** Classes without a rule here are denied for every client — default-deny is the engine's behaviour. */
export const accessController = new AccessController({ policies: [ServerContextPolicy, ...entityPolicies] });
